import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Pagination,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaExclamationCircle } from 'react-icons/fa';
import { Formik } from 'formik';
import Header from '~/Components/Header';
import MaskedInput from 'react-text-mask';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Field } from 'formik';
import api from '~/Services/api';
import { toast } from 'react-toastify';
import format from '~/Utils/format';
import { default as Selector } from 'react-select';
import DateMask from '~/Utils/DateMask';
import { MASK_DATE } from '~/constants';
import { salvarEmExcel } from '~/Utils/Helpers';

export const RelatorioComissoes = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [tipoComissaoSelecionado, setTipoComissaoSelecionado] = useState(null);
  const tiposComissao = [
    { value: '', label: 'Selecione...' },
    { value: 1, label: 'Corretor' },
    { value: 3, label: 'Imobiliára' },
  ];
  const totalPages = Math.ceil(dados.length / recordsPerPage);

  const dadosOrdenados = React.useMemo(() => {
    if (sortConfig.key) {
      const sorted = [...dados].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return dados;
  }, [dados, sortConfig]);

  const currentRecords = React.useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return dadosOrdenados.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [dadosOrdenados, currentPage, recordsPerPage]);

  const handleSort = column => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  useEffect(() => {
    const buscarEmpreendimentos = async () => {
      const response = await api.buscarEmpreendimentos();

      if (response) {
        setEmpreendimentos([
          { value: '', label: 'Selecione o empreendimento...' },
          ...response.map(item => ({
            value: item.codigo,
            label: `${item.codigo} - ${item.descricao}`,
          })),
        ]);
      }
    };

    buscarEmpreendimentos();
  }, []);

  const consultarRelatorioComissoes = async filtro => {
    try {
      setLoading(true);
      setTipoComissaoSelecionado(filtro.tipo_comissao);
      let response = await api.buscarRelatorioComissoes(filtro);
      if (response) {
        setDados(response);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  };

  const exportarExcel = async () => {
    try {
      setExportandoExcel(true);
      await salvarEmExcel(
        { dados: dados, tipo_comissao: tipoComissaoSelecionado },
        '/exportador/excel/realtorio-comissoes'
      );
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  return (
    <div>
      <Header title="Relatório de Comissões" />
      <Formik
        initialValues={{
          tipo_comissao: '',
          empreendimento: '',
          data_inicial: '',
          data_final: '',
        }}
        onSubmit={filtro => {
          consultarRelatorioComissoes(filtro);
        }}
      >
        {({ handleSubmit, setFieldValue, errors, touched, values }) => (
          <Form
            onSubmit={handleSubmit}
            className="container-fluid bg-white p-3"
          >
            <Row>
              <Col md={2}>
                <Form.Group controlId="tipo_comissao">
                  <Form.Label>Tipo Comissão</Form.Label>
                  <Selector
                    placeholder="Selecionar a comissão..."
                    isClearable={true}
                    options={tiposComissao}
                    value={tiposComissao.find(
                      option => option.value === values.tipo_comissao
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'tipo_comissao',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={6} md={4}>
                <Form.Group controlId="empreendimento">
                  <Form.Label>Empreendimento</Form.Label>
                  <Selector
                    noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                    placeholder="Selecionar empreendimento..."
                    isClearable={true}
                    options={empreendimentos}
                    value={empreendimentos.find(
                      option => option.value === values.empreendimento
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'empreendimento',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <DateMask
                  initialValue={values.data_inicial}
                  onValidate={value => setFieldValue('data_inicial', value)}
                  restrictToSqlMinDate
                  allowPast
                  allowFuture
                >
                  {({ dateError, dateValue, setDate }) => (
                    <Form.Group controlId="values.data_inicial">
                      <Form.Label>Data Inicial</Form.Label>
                      <MaskedInput
                        name="data_inicial"
                        type="tel"
                        guide={false}
                        className={`form-control ${
                          ((errors.data_inicial && touched.data_inicial) ||
                            dateError) &&
                          'is-invalid'
                        }`}
                        placeholder="Ex.: 01/01/2025"
                        mask={MASK_DATE}
                        value={dateValue}
                        onChange={e => setDate(e.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.data_inicial}
                      </Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {dateError}
                      </Form.Control.Feedback>
                      {errors.data_inicial && (
                        <div className="invalid-feedback d-block">
                          {errors.data_inicial}
                        </div>
                      )}
                    </Form.Group>
                  )}
                </DateMask>
              </Col>
              <Col md={2}>
                <DateMask
                  initialValue={values.data_final}
                  onValidate={value => setFieldValue('data_final', value)}
                  restrictToSqlMinDate
                  allowPast
                  allowFuture
                >
                  {({ dateError, dateValue, setDate }) => (
                    <Form.Group controlId="values.data_final">
                      <Form.Label>Data Final</Form.Label>
                      <MaskedInput
                        name="data_final"
                        type="tel"
                        guide={false}
                        className={`form-control ${
                          ((errors.data_final && touched.data_final) ||
                            dateError) &&
                          'is-invalid'
                        }`}
                        placeholder="Ex.: 01/01/2025"
                        mask={MASK_DATE}
                        value={dateValue}
                        onChange={e => setDate(e.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.data_final}
                      </Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {dateError}
                      </Form.Control.Feedback>
                      {errors.data_final && (
                        <div className="invalid-feedback d-block">
                          {errors.data_final}
                        </div>
                      )}
                    </Form.Group>
                  )}
                </DateMask>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Group>
                  <Form.Label>&nbsp;</Form.Label>
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100"
                    disabled={
                      loading ||
                      (errors.data_inicial && touched.data_inicial) ||
                      (errors.data_final && touched.data_final) ||
                      values.empreendimento === '' ||
                      values.tipo_comissao === ''
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <FaSearch className="mr-2" />
                        Buscar
                      </>
                    )}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {dados.length > 0 ? (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row className="mb-3">
              <Col>
                <span>Total de {dados.length} registro(s) encontrado(s).</span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="success"
                  onClick={exportarExcel}
                  disabled={exportandoExcel}
                >
                  {exportandoExcel ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </div>
          <div className="container-fluid bg-white table-responsive">
            <Table className="table table-sm table-striped table-bordered table-hover">
              <thead>
                <tr className="text-center font-size-14">
                  <th
                    onClick={() => handleSort('pre_venda')}
                    style={{ cursor: 'pointer' }}
                  >
                    Pré-Venda
                  </th>
                  <th
                    onClick={() => handleSort('data_venda')}
                    style={{ cursor: 'pointer' }}
                  >
                    Data Venda
                  </th>
                  <th
                    onClick={() => handleSort('nome_empreendimento')}
                    style={{ cursor: 'pointer' }}
                  >
                    Empreendimento - QD / LT
                  </th>
                  <th
                    onClick={() => handleSort('nome_cliente')}
                    style={{ cursor: 'pointer' }}
                  >
                    Cliente
                  </th>
                  <th
                    onClick={() => handleSort('valor_venda')}
                    style={{ cursor: 'pointer' }}
                  >
                    Valor Venda
                  </th>
                  <th
                    onClick={() => handleSort('sinal_efetivado')}
                    style={{ cursor: 'pointer' }}
                  >
                    Entrada
                  </th>
                  <th
                    onClick={() => handleSort('comissao_para')}
                    style={{ cursor: 'pointer' }}
                  >
                    Tipo Comissão
                  </th>
                  <th
                    onClick={() => handleSort('percentual_comissao')}
                    style={{ cursor: 'pointer' }}
                  >
                    Comissão
                  </th>
                  <th
                    onClick={() => handleSort('valor_comissao')}
                    style={{ cursor: 'pointer' }}
                  >
                    Valor Comissão
                  </th>
                  <th
                    onClick={() => handleSort('comissao_baixada')}
                    style={{ cursor: 'pointer' }}
                  >
                    Baixada
                  </th>
                  <th
                    onClick={() => handleSort('data_baixa_comissao')}
                    style={{ cursor: 'pointer' }}
                  >
                    Data Baixa
                  </th>
                  <th
                    onClick={() => handleSort('conta')}
                    style={{ cursor: 'pointer' }}
                  >
                    Conta
                  </th>
                  <th
                    onClick={() => handleSort('imob')}
                    style={{ cursor: 'pointer' }}
                  >
                    Imob
                  </th>
                </tr>
              </thead>
              <tbody className="text-center font-size-13">
                {currentRecords.map((item, index) => (
                  <tr
                    key={index}
                    style={{
                      fontWeight: 'normal',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s ease',
                    }}
                    className="text-left"
                    onClick={() => {}}
                  >
                    <td>{item.pre_venda}</td>
                    <td>{format.date(item.data_venda)}</td>
                    <td>
                      {item.nome_empreendimento} - {item.quadra} / {item.lote}
                    </td>
                    <td>{item.nome_cliente}</td>
                    <td>{format.money(item.valor_venda)}</td>
                    <td>{format.money(item.sinal_efetivado)}</td>
                    <td>{item.comissao_para}</td>
                    <td>{item.percentual_comissao} % </td>
                    <td>{format.money(item.valor_comissao)}</td>
                    <td>{item.comissao_baixada === 'S' ? 'Sim' : 'Não'}</td>
                    <td>
                      {item.data_baixa_comissao === null
                        ? 'Sem Baixa'
                        : format.date(item.data_baixa_comissao)}
                    </td>
                    <td>{item.conta}</td>
                    <td>{item.imob}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="container-fluid bg-white p-3">
            <Row className="mt-3 d-flex justify-content-between align-items-center">
              <Col xs="auto">
                {totalPages > 1 && (
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </Col>
            </Row>
          </div>
        </>
      ) : (
        !loading && (
          <div className="container-fluid bg-white p-5 mt-3 text-center">
            <Row>
              <Col>
                <FaExclamationCircle size={48} className="text-muted mb-3" />
                <h5 className="text-muted">Nenhum registro encontrado</h5>
                <p className="text-muted">
                  Utilize os filtros acima para buscar os dados.
                </p>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
};
